import { com } from '@eidu/web'
import { useInfiniteQuery, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import getPermissionPolicy from './api/entity/permissionPolicy/getPermissionPolicy'
import getPermissionPolicies from './api/entity/permissionPolicy/getPermissionPolicies'
import { PermissionPolicyDTO } from '../domain/permission/PermissionPolicyDTO'
import { permissionPolicyKeys } from './queryKeys'
import PermissionPolicyId = com.eidu.entity.permission.PermissionPolicyId

export type PermissionPoliciesPage = {
  rows: readonly PermissionPolicyDTO[]
  totalCount: number
}

export const fetchPermissionPoliciesPage = async (
  pageIndex: number,
  pageSize: number
): Promise<PermissionPoliciesPage> => {
  const response = await getPermissionPolicies({ pageIndex, pageSize })
  return {
    rows: response.documents.asJsReadonlyArrayView(),
    totalCount: response.totalCount,
  }
}

export type UsePermissionPoliciesInfiniteResult = {
  policies: readonly PermissionPolicyDTO[]
  totalCount: number
  hasMore: boolean
  loading: boolean
  loadMore: () => void
  error: Error | null
}

export const usePermissionPoliciesInfinite = (pageSize: number = 50): UsePermissionPoliciesInfiniteResult => {
  const query = useInfiniteQuery({
    queryKey: [...permissionPolicyKeys.all, 'infinite', pageSize] as const,
    queryFn: ({ pageParam }: { pageParam: number }) => fetchPermissionPoliciesPage(pageParam, pageSize),
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((sum, page) => sum + page.rows.length, 0)
      return loaded < lastPage.totalCount ? allPages.length : undefined
    },
    initialPageParam: 0,
  })

  const policies = useMemo(
    () => query.data?.pages.flatMap((page) => page.rows) ?? [],
    [query.data?.pages, query.dataUpdatedAt]
  )

  const loadMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetchingNextPage) query.fetchNextPage()
  }, [query])

  return {
    policies,
    totalCount: query.data?.pages[0]?.totalCount ?? 0,
    hasMore: query.hasNextPage ?? false,
    loading: query.isLoading || query.isFetchingNextPage || query.isRefetching,
    loadMore,
    error: query.error as Error | null,
  }
}

export const fetchPermissionPolicy = async (id: PermissionPolicyId): Promise<PermissionPolicyDTO | null> =>
  (await getPermissionPolicy({ id })) ?? null

export const usePermissionPolicy = (id: PermissionPolicyId | undefined) => {
  const query = useQuery({
    queryKey: id ? permissionPolicyKeys.detail(id) : permissionPolicyKeys.all,
    queryFn: () => fetchPermissionPolicy(id!),
    enabled: !!id,
  })
  return {
    policy: id ? query.data : undefined,
    isLoading: query.isLoading,
    error: query.error,
  }
}

export const usePermissionPoliciesByIds = (ids: ReadonlyArray<PermissionPolicyId>) =>
  useQueries({
    queries: ids.map((id) => ({
      queryKey: permissionPolicyKeys.detail(id),
      queryFn: () => fetchPermissionPolicy(id),
    })),
    combine: (queries) => ({
      policies: queries.flatMap((q) => (q.data ? [q.data] : [])),
      isLoading: queries.some((q) => q.isLoading),
      error: queries.find((q) => q.error)?.error ?? null,
    }),
  })

export const useFetchPermissionPolicy = () => {
  const queryClient = useQueryClient()
  return useCallback(
    (id: PermissionPolicyId) =>
      queryClient.fetchQuery({
        queryKey: permissionPolicyKeys.detail(id),
        queryFn: () => fetchPermissionPolicy(id),
      }),
    [queryClient]
  )
}
